"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SearchService = exports.FilterState = void 0;
const tslib_1 = require("tslib");
const core_api_1 = require("@ikegps/core-api");
const core_shared_1 = require("@ikegps/core-shared");
const lodash_1 = require("lodash");
const mobx_1 = require("mobx");
const notification_service_1 = require("../components/notification.toast/notification.service");
const i18n_1 = require("../i18n");
const tag_sort_state_1 = require("../state/tag.sort.state");
const store_1 = require("../store");
const index_1 = require("./index");
class FilterState {
    constructor() {
        this.tagFilters = mobx_1.observable.array([], { deep: false });
    }
    addTagFilter(tagFilter) {
        const existing = this.tagFilters.find((tag) => tag.objectId === tagFilter.objectId);
        if (existing == null) {
            this.tagFilters.push(tagFilter);
        }
        this.filter = null;
    }
    setFilter(value) {
        this.filter = value;
    }
    hasFilter() {
        return this.tagFilters.length > 0 || (this.filter != null && this.filter.trim().length > 0);
    }
    matches(tag) {
        if (tag == null) {
            return false;
        }
        if (!this.hasFilter()) {
            return true;
        }
        if (this.tagFilters.length > 0) {
            return this.tagFilters.find((t) => t.objectId === tag.objectId) != null;
        }
        return tag.name.toLowerCase().includes(this.filter.trim().toLowerCase());
    }
    clear() {
        this.filter = null;
        this.tagFilters.clear();
    }
}
exports.FilterState = FilterState;
tslib_1.__decorate([
    mobx_1.observable
], FilterState.prototype, "filter", void 0);
tslib_1.__decorate([
    mobx_1.action
], FilterState.prototype, "addTagFilter", null);
tslib_1.__decorate([
    mobx_1.action
], FilterState.prototype, "setFilter", null);
tslib_1.__decorate([
    mobx_1.action
], FilterState.prototype, "clear", null);
const dynamicDebounce = (debouncedAction, getDebounceMs) => {
    let cancelableTimeout;
    return () => {
        if (cancelableTimeout) {
            clearTimeout(cancelableTimeout);
        }
        cancelableTimeout = setTimeout(() => {
            debouncedAction();
        }, getDebounceMs());
    };
};
const MAXIMUM_DELAY_MS = 2000;
const MINIMUM_DELAY_MS = 500;
class SearchService {
    getDebounceDelay() {
        const charLength = (0, lodash_1.get)(this.inputfilterState, ['filter', 'length']);
        const evaluatedDebounce = charLength ? MAXIMUM_DELAY_MS - (charLength - 1) * 200 : MINIMUM_DELAY_MS;
        return (0, lodash_1.clamp)(evaluatedDebounce, MINIMUM_DELAY_MS, MAXIMUM_DELAY_MS);
    }
    constructor() {
        this.__priority = 100;
        this.inFlight = false;
        this.collectionRevisions = mobx_1.observable.array([], { deep: false });
        this.inputfilterState = new FilterState();
        this.filterState = new FilterState();
    }
    init() {
        const search = dynamicDebounce(this.search.bind(this), this.getDebounceDelay.bind(this));
        (0, mobx_1.reaction)((_) => this.inputfilterState.filter, () => {
            if ((0, lodash_1.get)(this.inputfilterState, ['filter', 'length']) >= core_shared_1.IkeConfigStore.MAX_SEARCH_LENGTH) {
                this.setInFlight(false);
                notification_service_1.NotificationService.warn({ key: i18n_1.T.SEARCH_ERROR });
            }
            else {
                this.setInFlight(true);
                search();
            }
        });
        (0, mobx_1.reaction)((_) => index_1.Services.Department.current, (_) => this.reset());
    }
    reset() {
        this.inputfilterState.clear();
        this.filterState.clear();
    }
    includesTag(tag) {
        if (this.filterState.hasFilter()) {
            return this.tags.indexOf(tag) > -1;
        }
        return true;
    }
    get tags() {
        if (this.filterState.hasFilter()) {
            return store_1.AppStore.Tag.all.filter((tag) => this.filterState.matches(tag) || this.collectionSummaries[tag.objectId] != null);
        }
        return store_1.AppStore.Tag.all;
    }
    get collectionSummaries() {
        const map = {};
        for (const collectionRevision of this.collectionRevisions) {
            const collectionSummary = store_1.AppStore.CollectionSummary.get(collectionRevision);
            if (collectionSummary == null) {
                continue;
            }
            if (collectionSummary.tags.indexOf(core_api_1.SystemTag.deleted) > -1) {
                continue;
            }
            for (const tag of collectionSummary.tags) {
                var mapTag = map[tag];
                if (mapTag == null) {
                    map[tag] = [collectionSummary];
                }
                else {
                    mapTag.push(collectionSummary);
                }
            }
        }
        for (const tagId of Object.keys(map)) {
            const summaries = map[tagId];
            tag_sort_state_1.TagSortState.sortSummaries(summaries);
        }
        return map;
    }
    setInFlight(inFlight) {
        this.inFlight = inFlight;
    }
    search() {
        if (!this.inputfilterState.hasFilter()) {
            this.collectionRevisions.clear();
            this.filterState.setFilter(this.inputfilterState.filter);
            this.setInFlight(false);
            return Promise.resolve();
        }
        this.setInFlight(true);
        const filter = this.inputfilterState.filter;
        return store_1.AppStore.Collection.search(filter).then((results) => {
            // Old query
            if (filter !== this.inputfilterState.filter) {
                return null;
            }
            const revisions = results.map((result) => result.revision);
            return Promise.all(revisions.map((revision) => this.prefetchCollectionSummary(revision))).then(() => this.setCollectionRevisions(filter, revisions));
        });
    }
    setCollectionRevisions(filter, collectionRevisions) {
        this.collectionRevisions.replace(collectionRevisions);
        this.filterState.setFilter(filter);
        this.setInFlight(false);
    }
    prefetchCollectionSummary(revision) {
        const collectionSummary = store_1.AppStore.CollectionSummary.get(revision);
        if (collectionSummary != null) {
            return collectionSummary;
        }
        return store_1.AppStore.CollectionSummary.fetch(revision);
    }
}
exports.SearchService = SearchService;
tslib_1.__decorate([
    mobx_1.observable
], SearchService.prototype, "inFlight", void 0);
tslib_1.__decorate([
    mobx_1.observable
], SearchService.prototype, "collectionRevisions", void 0);
tslib_1.__decorate([
    mobx_1.computed
], SearchService.prototype, "tags", null);
tslib_1.__decorate([
    mobx_1.computed
], SearchService.prototype, "collectionSummaries", null);
tslib_1.__decorate([
    mobx_1.action
], SearchService.prototype, "setInFlight", null);
tslib_1.__decorate([
    mobx_1.action
], SearchService.prototype, "search", null);
tslib_1.__decorate([
    mobx_1.action
], SearchService.prototype, "setCollectionRevisions", null);
//# sourceMappingURL=search.service.js.map